import { ApiError, apiCall, apiCallText } from "@/api/client";
import type {
  AutoDeployPolicy,
  Deployment,
  EmergencyAccount,
  ListDeploymentsResponse,
  ListEmergencyAccountsResponse,
  SsoMode,
  ListTenantsResponse,
  OnboardingUrlResponse,
  TenantDetail,
  TenantMetadata,
  TenantStage,
} from "@/types/api";

export function listTenants(token: string, status?: string) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return apiCall<ListTenantsResponse>(`/tenants${qs}`, { token });
}

export function getTenant(token: string, tenantId: string) {
  return apiCall<TenantDetail>(`/tenants/${encodeURIComponent(tenantId)}`, { token });
}

// ``domain`` and ``own_partnership_id`` are intentionally absent — they are
// derived from tenant_id by the backend model_validator and any value sent by
// the client is silently overridden. ``tenant_id`` is optional on create: when
// missing the backend slugifies it from ``display_name``.
//
// ``enabled_options`` / ``allowed_*`` moved to PutStageInput (per-stage
// scoping) — they are no longer accepted on the tenant body.
export interface CreateTenantInput {
  tenant_id?: string;
  display_name: string;
  // Admin contact — required by the backend on create; the deploy webhook
  // uses it to invite this address into the stage's Cognito pool after
  // every successful deploy (idempotent on re-deploy).
  admin_email: string;
  admin_given_name: string;
  admin_family_name: string;
  status?: string;
  // Per-tenant gate for the dataplane cost-estimation GUI card (effective on
  // next deploy). Accepted at create and patchable afterwards.
  cost_estimation_ui_enabled?: boolean;
  // Requires a second factor for every user of the tenant, on every stage.
  mfa_required?: boolean;
}

export function createTenant(token: string, input: CreateTenantInput) {
  return apiCall<TenantMetadata>("/tenants", {
    method: "POST",
    body: input,
    token,
  });
}

// One directory on the wire (snake_case, mirrors ``IdentityProviderConfig``).
export interface IdentityProviderInput {
  id: string;
  display_name: string;
  protocol: "saml";
  // Exactly one: the backend refuses both or neither.
  metadata_url?: string | null;
  metadata_file?: string | null;
  email_domains: string[];
  role_source: "invitation" | "directory";
  claims: { email: string; roles?: string };
}

// Patchable METADATA fields (snake_case wire keys, mirrors the backend
// METADATA_PATCHABLE_FIELDS allow-list). Superset of the creatable fields plus
// the operator-toggleable booleans.
export interface TenantMetadataPatch {
  display_name?: string;
  admin_email?: string;
  admin_given_name?: string;
  admin_family_name?: string;
  debug?: boolean;
  // Toggles the dataplane cost-estimation GUI card (effective on next deploy).
  cost_estimation_ui_enabled?: boolean;
  // Requires a second factor for every user of the tenant, on every stage.
  // Applied to the Cognito pool synchronously — the PATCH fails if Cognito
  // refuses, rather than leaving the sheet claiming a protection nobody has.
  mfa_required?: boolean;
  // The whole list of the tenant's directories, applied to the Cognito pool
  // synchronously (created, updated, deleted). Only the identity-providers page
  // sends it: any other save leaves the pool alone.
  identity_providers?: IdentityProviderInput[];
  // How users sign in: ``exclusive`` leaves only the emergency accounts a
  // password. The backend refuses (409, ``details.blockers``) a tenant that
  // does not meet the conditions.
  sso_mode?: SsoMode;
}

/**
 * What the server lists as preventing a change (a 409 with ``details.blockers``: a directory-only
 * sign-in that lacks an emergency account, the second factor, a directory on a stage), or ``null``
 * for any other error.
 */
export function blockersOf(error: unknown): string[] | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const details = (error.body as { details?: { blockers?: unknown } } | null)?.details;
  const blockers = details?.blockers;
  return Array.isArray(blockers) && blockers.every((b) => typeof b === "string") && blockers.length > 0
    ? (blockers as string[])
    : null;
}

export function listEmergencyAccounts(token: string, tenantId: string) {
  return apiCall<ListEmergencyAccountsResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/emergency-accounts`,
    { token },
  );
}

export function addEmergencyAccount(token: string, tenantId: string, email: string) {
  return apiCall<EmergencyAccount>(`/tenants/${encodeURIComponent(tenantId)}/emergency-accounts`, {
    method: "POST",
    body: { email },
    token,
  });
}

export function removeEmergencyAccount(token: string, tenantId: string, username: string) {
  return apiCall<void>(
    `/tenants/${encodeURIComponent(tenantId)}/emergency-accounts/${encodeURIComponent(username)}`,
    { method: "DELETE", token },
  );
}

export function updateTenantMetadata(
  token: string,
  tenantId: string,
  patch: TenantMetadataPatch,
) {
  return apiCall<TenantMetadata>(`/tenants/${encodeURIComponent(tenantId)}`, {
    method: "PATCH",
    body: patch,
    token,
  });
}

// ``aws_account_id`` and ``deploy_role_arn`` are intentionally absent — they
// are filled by /onboarding/claim when the operator launches the bootstrap
// stack in the tenant account (auto-service flow, post auth double-hop).
// ``url_prefix`` is derived server-side. The Cognito pool is per-tenant (set
// at POST /tenants); stages bind into it automatically via 4 RBAC groups
// + 1 SPA App Client.
export interface PutStageInput {
  aws_region?: string;
  is_production?: boolean;
  vpc_cidr?: string;
  // Operator-pinned customer-gateway Docker build_id, or null to follow
  // latest. Backend ``TenantStage`` validator rejects malformed values
  // (must match ``build-YYYYMMDD-N``); empty string is normalised to null
  // server-side too. Omit the key entirely for a partial PATCH that leaves
  // the field untouched.
  pinned_customer_gw_build_id?: string | null;
  // Per-stage authorization to run beta-channel customer-gateway builds
  // (gw-docker branches other than main). The form always sends an explicit
  // bool; omitting the key on an ad-hoc partial PUT preserves the stored
  // value (server-managed field). While false the backend refuses a beta
  // pin (400) and the dataplane hides/refuses beta builds.
  allow_beta_gateway_builds?: boolean;
  // Per-stage automatic customer-gateway updates: while true the dataplane's
  // 15-minute drift sweep triggers update jobs toward the stage's resolved
  // target build. Same partial-PUT semantics as the beta flag (server-managed
  // field — omitting the key preserves the stored value).
  auto_update_customer_gateways?: boolean;
  // Per-stage scoping (moved off the tenant). ``enabled_options`` is GUI-only.
  // ``allowed_*`` are keyed by model slug with a list of allowed versions —
  // ``"*"`` is the wildcard "every version of this model". The form currently
  // only emits wildcards; a future iteration can let the operator pick
  // specific semver versions.
  enabled_options?: string[];
  allowed_gateway_types?: Record<string, string[]> | null;
  allowed_customer_instances?: Record<string, string[]> | null;
  // Per-stage gate for the dataplane Diagnostics GUI section (effective on next
  // deploy). The form always sends an explicit bool; omitting the key on an
  // ad-hoc partial PUT preserves the stored value (server-managed field).
  diagnostics_ui_enabled?: boolean;
  // Per-stage gate for the dataplane in-GUI bug tracker (effective on next
  // deploy; also enforced at ingestion by the control plane). Same partial-PUT
  // semantics as ``diagnostics_ui_enabled`` (server-managed field).
  bug_tracker_enabled?: boolean;
  // Per-stage gate for the companion apps screens of the dataplane GUI (paired
  // TVs, pairing approval, profile QR code); effective on next deploy. Same
  // partial-PUT semantics as ``diagnostics_ui_enabled`` (server-managed field).
  companion_apps_enabled?: boolean;
  // Per-stage gate for the dataplane GrandCentral MCP server. The materializer
  // turns it into ``"MCP"`` in enabled_options, which gates the MCP stack
  // deployment. Same partial-PUT semantics as the other gates (server-managed
  // field).
  mcp_enabled?: boolean;
  // Audience Measurement option: the collection (CloudFront fields, sensitive
  // fact columns) is deployed on the next deploy. Same partial-PUT semantics as
  // the other gates (server-managed field).
  audience_measurement_enabled?: boolean;
  // How long the facts keep full IP addresses, 15..90 days; ``null`` = 90. An
  // activated preset may require more (the API refuses a lower value).
  audience_measurement_ip_retention_days?: number | null;
  // Ids of the measurement presets activated on the stage. The API refuses a
  // non-empty list without the option: the form sends ``[]`` when it is off.
  measurement_presets?: string[];
  // Per-stage raw-PCM S3 retention (days), effective on next deploy. Drives the
  // dataplane raw audio bucket lifecycle (= DVR / catch-up window). ``null``
  // (or empty in the form) means "inherit" the tenant default. The form always
  // sends an explicit value or null; omitting the key on an ad-hoc partial PUT
  // preserves the stored override (server-managed field).
  media_retention_days?: number | null;
  // Per-stage audience live threshold (ms), effective on next deploy. ``null``
  // means "inherit" (tenant default, then the dataplane's 30 000). Same
  // contract as ``media_retention_days``: explicit value or null from the form,
  // omission on an ad-hoc partial PUT preserves the stored override.
  audience_live_threshold_ms?: number | null;
  // Continuous-deployment policy (none / latest / latest_prod_only). The form
  // always sends an explicit value; omitting the key on an ad-hoc partial PUT
  // preserves the stored policy (server-managed field). Backend rejects
  // ``latest`` on a production stage.
  auto_deploy?: AutoDeployPolicy;
  // AWS regions a customer gateway on this stage may ingest from (data-path
  // regionalization). The dataplane spins up a regional encoder stack + bucket
  // per region and validates a gateway's region against this set (the deploy
  // region is always implicitly available, so list only the EXTRA regions).
  // Backend validates each as an AWS region id and dedups. The form sends an
  // explicit list; omitting the key on a partial PUT preserves it (server-managed).
  enabled_ingest_regions?: string[];
  // Ids of the tenant's identity providers (TenantMetadata.identityProviders)
  // offered on this stage's sign-in page. Backend rejects (400) any id the
  // tenant does not declare. The form sends an explicit array; omitting the
  // key on an ad-hoc partial PUT preserves the stored value (server-managed
  // field).
  enabled_identity_providers?: string[];
}

// PUT /stages/{stage} returns the saved stage plus a ``needsProvisioning``
// flag and (when the stage is awaiting claim) a ``launchStackUrl`` +
// ``expiresAt`` so the GUI can render the onboarding panel inline without an
// extra GET /onboarding-url round-trip.
//
// Wire keys are camelCase like every other field — convert_keys_to_camel in
// response.py rewrites the Lambda's snake_case body.
export interface PutStageResponse extends TenantStage {
  needsProvisioning: boolean;
  launchStackUrl?: string;
  expiresAt?: number;
}

export function putStage(
  token: string,
  tenantId: string,
  stage: string,
  input: PutStageInput,
  productionKey?: string,
) {
  return apiCall<PutStageResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/stages/${encodeURIComponent(stage)}`,
    { method: "PUT", body: input, token, headers: productionKeyHeader(productionKey) },
  );
}

// Écriture SÉPARÉE du PUT de stage : le backend garde ``license_status`` hors
// des champs patchables pour qu'un PUT partiel ne puisse pas re-licencier un
// client suspendu par omission. La clé de production est exigée sur un stage de
// production, comme pour destroy et delete.
export function setLicenseStatus(
  token: string,
  tenantId: string,
  stage: string,
  status: "active" | "suspended",
  productionKey?: string,
) {
  return apiCall<TenantStage>(
    `/tenants/${encodeURIComponent(tenantId)}/stages/${encodeURIComponent(stage)}/license-status`,
    {
      method: "PUT",
      body: { license_status: status },
      token,
      headers: productionKeyHeader(productionKey),
    },
  );
}

export function triggerDeploy(
  token: string,
  tenantId: string,
  stage: string,
  // build_id selects which dataplane build to deploy (from the build
  // catalogue). Omitted → the control plane deploys the latest production
  // build (backward-compatible "latest-master" behaviour).
  buildId?: string,
) {
  return apiCall<Deployment>(
    `/tenants/${encodeURIComponent(tenantId)}/deploy`,
    { method: "POST", body: { stage, build_id: buildId }, token },
  );
}

// The production key never travels in the body: a body can end up in a log,
// a header is scrubbed in one place. Undefined on non-production targets.
export function productionKeyHeader(key?: string) {
  return key ? { "X-Hapsout-Production-Key": key } : undefined;
}

// Body is enforced server-side (cf. destroy_trigger Lambda) — pass it through
// untouched from the modal so a misclick can never produce a destroy.
export interface TriggerDestroyBody {
  confirm: string;
  production_acknowledged?: boolean;
}

export function triggerDestroy(
  token: string,
  tenantId: string,
  stage: string,
  body: TriggerDestroyBody,
  productionKey?: string,
) {
  return apiCall<Deployment>(
    `/tenants/${encodeURIComponent(tenantId)}/stages/${encodeURIComponent(stage)}/destroy`,
    { method: "POST", body, token, headers: productionKeyHeader(productionKey) },
  );
}

// Reads the current Launch Stack URL for a stage still in ``awaiting_claim``.
// The backend returns the same token minted by put_stage (no re-mint) along
// with its TTL. Re-minting is done by re-PUTing the stage with an empty body.
export function getOnboardingUrl(
  token: string,
  tenantId: string,
  stage: string,
) {
  return apiCall<OnboardingUrlResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/stages/${encodeURIComponent(stage)}/onboarding-url`,
    { token },
  );
}

// The SAML SP metadata XML document — an alternative to the customer's IT copy-pasting the Entity ID
// and Reply URL fields by hand: uploading this one file into the directory (Entra "Charger le fichier
// de métadonnées") fills both at once. 409 (surfaced as an ``ApiError``) until the tenant's pool and
// login domain are both ready, same as the two fields being ``null`` on the tenant metadata.
export function getSamlMetadata(token: string, tenantId: string) {
  return apiCallText(`/tenants/${encodeURIComponent(tenantId)}/saml-metadata`, { token });
}

export function archiveTenant(token: string, tenantId: string) {
  return apiCall<TenantMetadata>(`/tenants/${encodeURIComponent(tenantId)}`, {
    method: "DELETE",
    token,
  });
}

export interface PurgeTenantResponse {
  tenantId: string;
  purged: boolean;
}

// The backend now requires ``confirm`` — the phrase the modal already asks the
// operator to type was never sent.
export interface ConfirmBody {
  confirm: string;
}

// Hard-delete an archived tenant: drops metadata, every environment, every
// deployment, and every related secret. Backend rejects unless the tenant is
// already archived.
export function purgeTenant(
  token: string,
  tenantId: string,
  body: ConfirmBody,
  productionKey?: string,
) {
  return apiCall<PurgeTenantResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/purge`,
    { method: "DELETE", body, token, headers: productionKeyHeader(productionKey) },
  );
}

// Recovery endpoint for tenants whose attach_custom_domain failed during
// _create_tenant. Idempotent — safe to call on an already-active tenant.
export function retryCognitoDomain(token: string, tenantId: string) {
  return apiCall<TenantMetadata>(
    `/tenants/${encodeURIComponent(tenantId)}/cognito-domain/retry`,
    { method: "POST", token },
  );
}

export interface DeleteStageResponse {
  tenantId: string;
  stage: string;
  deleted: boolean;
}

export function deleteStage(
  token: string,
  tenantId: string,
  stage: string,
  body: ConfirmBody,
  productionKey?: string,
) {
  return apiCall<DeleteStageResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/stages/${encodeURIComponent(stage)}`,
    { method: "DELETE", body, token, headers: productionKeyHeader(productionKey) },
  );
}

// Force-destroy: dispatches the same destroy-tenant.yml workflow as
// ``triggerDestroy`` but with ``inputs.force=true``. The dataplane workflow
// then tolerates broken CFN state (FORCE_DELETE_STACK fallback on stuck
// stacks) and the CP terminal callback purges the STAGE row outright
// instead of flipping ``is_destroyed``. Same defensive contract as
// ``triggerDestroy`` (confirm string + production_acknowledged) — the
// extra power doesn't relax the misclick guard. Returns a Deployment
// (202 Accepted) so the caller can plug into the same progress UI as the
// safe destroy.
export function forceDeleteStage(
  token: string,
  tenantId: string,
  stage: string,
  body: TriggerDestroyBody,
  productionKey?: string,
) {
  return apiCall<Deployment>(
    `/tenants/${encodeURIComponent(tenantId)}/stages/${encodeURIComponent(stage)}/force`,
    { method: "DELETE", body, token, headers: productionKeyHeader(productionKey) },
  );
}

export function listDeployments(
  token: string,
  tenantId: string,
  opts?: { stage?: string; limit?: number },
) {
  const params = new URLSearchParams();
  if (opts?.stage) params.set("stage", opts.stage);
  if (opts?.limit) params.set("limit", String(opts.limit));
  const qs = params.toString() ? `?${params.toString()}` : "";
  return apiCall<ListDeploymentsResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/deployments${qs}`,
    { token },
  );
}

export function getDeployment(token: string, tenantId: string, runId: string) {
  return apiCall<Deployment>(
    `/tenants/${encodeURIComponent(tenantId)}/deployments/${encodeURIComponent(runId)}`,
    { token },
  );
}

// Operator escape hatch for a DEPLOYMENT row stuck at ``triggered`` /
// ``in_progress`` — typically when the GHA workflow_dispatch landed but the
// job never actually ran (billing block, runner offline). Idempotent on
// terminal status: backend returns the row as-is without raising. The
// returned Deployment carries the new ``failed`` status and an audit-friendly
// error_message naming the JWT subject that clicked Cancel.
export function cancelDeployment(token: string, tenantId: string, runId: string) {
  return apiCall<Deployment>(
    `/tenants/${encodeURIComponent(tenantId)}/deployments/${encodeURIComponent(runId)}/cancel`,
    { method: "POST", token },
  );
}
